import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import config from 'config.json';
import useFetch from 'hooks/useFetch';

interface Bookings {
    id: number;
    codigo_hotel: string;
    descripcion_hotel: string;
    direcion: string;
    ciudad: string;
    fecha_hora: string;
    fecha_inicio: string;
    fecha_fin: string;
    cant_personas: number;
    estado_reserva_enum: string;
    monto_total: number;
    monto_abonado: number;
    estado_confirmacion: string;
    origen_cancelacion: string;
    sub_estado:string;
}

interface PastBookingsTableProps {
    reservas: Bookings[];
}

const PastBookingsTable: React.FC<PastBookingsTableProps> = ({ reservas }) => {
    const router = useNavigate();
    const [selectedReservation, setSelectedReservation] = useState<Bookings | null>(null);
    const [isEditModalOpen, setEditModalOpen] = useState(false);
    const [isCancelModalOpen, setCancelModalOpen] = useState(false);
    const [cancelReservationRequest, setCancelReservationRequest] = useState({
        id_reserva: '',
        motivo: '',
    });
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');

    const { data, isLoading, error, fetchData } = useFetch(
        'PUT',
        `${config.backend_url}/setia/reservas/cancelar`,
        cancelReservationRequest,
        { 'Content-Type': 'application/json' }
    );

    useEffect(() => {
        if (data && !error) {
            setSuccessMessage('Reserva cancelada con éxito.');
            //setCancelModalOpen(false);
            // Aquí puedes refrescar las reservas
        } else if (error) {
            setErrorMessage('Ocurrió un error al cancelar la reserva.');
        }
    }, [data, error]);

    const handleEdit = (reservation: Bookings) => {
        router(`/edit-reservation?reservationId=${reservation.id}`);
    };

    const handleCancel = (reservation: Bookings) => {
        setSelectedReservation(reservation);
        setCancelModalOpen(true);
        setSuccessMessage('');
        setErrorMessage('');
        setCancelReservationRequest({
            id_reserva: reservation.id.toString(),
            motivo: '',
        });
    };

    const handleCancelReservation = () => {
        fetchData();
    };

    const filterPastReservations = (reservas: Bookings[]): Bookings[] => {
        const currentDate = new Date();
        return reservas.filter(reserva => {
            const fechaFin = new Date(reserva.fecha_fin);
            return fechaFin < currentDate;
        });
    };

    const filteredReservations = filterPastReservations(reservas);

    const months = [
        'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio',
        'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
    ];

    const formatDate = (dateString: string): string => {
        const [year, month, day] = dateString.split('-');
        const monthIndex = parseInt(month, 10) - 1;
        const monthName = months[monthIndex];
        return `${day} ${monthName.charAt(0).toUpperCase() + monthName.slice(1)} ${year}`;
    };

    const needsToPay = (reservation: Bookings) => {
        if (reservation.monto_abonado < reservation.monto_total) {
            return (
                <button onClick={() => router(`/booking-payments?booking=${reservation.id}`)} className="bg-yellow-500 text-white px-2 py-1 rounded-full">Pagar</button>
            );
        } else {
            return null;
        }
    };

    const statusMap = (reservation: Bookings) => {
        switch (reservation.estado_reserva_enum) {
            case 'CONFIRMADA':
                return (
                    <span className="bg-green-100 text-green-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded-full dark:bg-green-200 dark:text-green-900">Confirmada</span>
                );
            case 'CANCELADA':
                return (
                    <span className="bg-red-100 text-red-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded-full dark:bg-red-200 dark:text-red-900">Cancelada</span>
                );
            case 'RECHAZO':
                return (
                    <span className="bg-yellow-100 text-yellow-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded-full dark:bg-yellow-200 dark:text-yellow-900">Rechazada</span>
                );
            case 'PAGO_PENDIENTE':
                return (
                    <span className="bg-blue-100 text-blue-800 text-xs font-medium mr-2 px-2.5 py-0.5 rounded-full dark:bg-blue-200 dark:text-blue-900">Pendiente</span>
                );
            default:
                return 'Desconocido';
        }
    };

    const renderActions = (reservation: Bookings) => {
        if (reservation.estado_reserva_enum === 'CONFIRMADA') {
            return (
                <div className="flex space-x-1 py-2 justify-center">
                    {needsToPay(reservation)}
                    <button
                        className="bg-blue-500 text-white px-2 py-1 rounded-full"
                        onClick={() => handleEdit(reservation)}
                    >
                        Editar
                    </button>
                    <button
                        className="bg-red-500 text-white px-2 py-1 rounded-full"
                        onClick={() => handleCancel(reservation)}
                    >
                        Cancelar
                    </button>
                </div>
            );
        } else {
            return (
                <div className="flex space-x-1 py-2 justify-center">
                    <span>-</span>
                </div>
            );
        }
    };

    const renderRazon = (reservation: Bookings) => {
        switch (reservation.estado_reserva_enum) {
            case 'CONFIRMADA':
                return (
                    <span >{reservation.sub_estado.split("_")[1] ?? "-"}</span>
                );
            case 'CANCELADA':
                return (
                    <span >{reservation.sub_estado.split("_")[1] ?? "-"}</span>
                );
            case 'RECHAZO':
                return (
                    <span >{reservation.sub_estado.split("_")[1] ?? "-"}</span>
                );
            case 'PAGO_PENDIENTE':
                return (
                    <span >{reservation.sub_estado.split("_")[1] ?? "-"}</span>
                );
            default:
                return 'Desconocido';
        }
    }

    return (
        <div className="overflow-x-auto border rounded-xl p-6 bg-white">
            <table className="min-w-full bg-white">
                <thead>
                    <tr className="">
                        <th className="py-2 px-4 border-b font-semibold">N° Reserva</th>
                        <th className="py-2 px-4 border-b font-semibold">Hotel</th>
                        <th className="py-2 px-4 border-b font-semibold">Checkin</th>
                        <th className="py-2 px-4 border-b font-semibold">Checkout</th>
                        <th className="py-2 px-4 border-b font-semibold">Estado </th>
                        <th className="py-2 px-4 border-b font-semibold">Sub estado</th>
                        <th className="py-2 px-4 border-b font-semibold">Abonado</th>
                        <th className="py-2 px-4 border-b font-semibold">Monto</th>
                        {/* <th className="py-2 px-4 border-b font-semibold">Acciones</th> */}
                    </tr>
                </thead>
                <tbody className="text-sm">
                    {filteredReservations.map((reserva) => (
                        <tr key={reserva.id}>
                            <td className="py-2 px-4 border-b text-center">{reserva.id}</td>
                            <td className="py-2 px-4 border-b text-center">{reserva.descripcion_hotel} - {reserva.ciudad}</td>
                            <td className="py-2 px-4 border-b text-center">{formatDate(reserva.fecha_inicio)}</td>
                            <td className="py-2 px-4 border-b text-center">{formatDate(reserva.fecha_fin)}</td>
                            <td className="py-2 px-4 border-b text-center">{statusMap(reserva)}</td>
                            <td className="py-2 px-4 border-b text-center">{renderRazon(reserva)}</td>
                            <td className="py-2 px-4 border-b text-center">{reserva.monto_abonado.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
                            <td className="py-2 px-4 border-b text-center">{reserva.monto_total.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
                           {/*  <td className="py-2 px-4 border-b flex space-x-2 justify-center">
                                {renderActions(reserva)}
                            </td> */}
                        </tr>
                    ))}
                </tbody>
            </table>

            {isCancelModalOpen && selectedReservation && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
                    <div className="bg-white p-4 rounded shadow-lg">
                        <h2 className="text-xl mb-4">Cancelar Reserva</h2>

                        <p className="mb-4">
                            ¿Estás seguro de cancelar tu reserva en {selectedReservation.descripcion_hotel} - {selectedReservation.ciudad}?
                        </p>
                        <input
                            required
                            disabled={successMessage !== ''}
                            type="text"
                            placeholder="Motivo de cancelación"
                            className="mb-4 p-2 border rounded-full w-full"
                            value={cancelReservationRequest.motivo}
                            onChange={(e) => setCancelReservationRequest({ ...cancelReservationRequest, motivo: e.target.value })}
                        />
                        <div className="flex justify-end">
                            <button
                                className="bg-gray-500 text-white px-4 py-2 rounded-full mr-2"
                                onClick={() => setCancelModalOpen(false)}
                            >
                                Cerrar
                            </button>
                            <button
                                disabled={successMessage !== '' || cancelReservationRequest.motivo === ''}
                                className={`bg-red-500 text-white px-4 py-2 rounded-full ${cancelReservationRequest.motivo === '' ? 'opacity-50' : ''}`}
                                onClick={handleCancelReservation}
                            >
                                Cancelar Reserva
                            </button>
                        </div>
                        {errorMessage && <p className="text-red-500 mt-2">{errorMessage}</p>}
                        {successMessage && <p className="text-green-500 mt-2">{successMessage}</p>}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PastBookingsTable;